import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild, inject } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { SessionService } from '../../services/session.service';
import { NgxTgModule } from '@worthy-ventures/metaglotta-ngx';

/**
 * The single section nav for every signed-in role — the dashboard tabs and the
 * admin tabs in one bar (was two separate bars, app-admin-nav and
 * app-dashboard-nav, each visible only on its own pages).
 *
 * Each getter below mirrors the route guard for the tabs it controls, so a tab
 * is shown only where navigating to it actually succeeds. Keep them in step: a
 * looser getter means a dead tab, a stricter one hides a page the user may use.
 */
@Component({
    selector: 'app-admin-nav',
    imports: [RouterLink, RouterLinkActive, NgxTgModule],
    templateUrl: './admin-nav.component.html',
    styleUrl: './admin-nav.component.scss',
})
export class AdminNavComponent implements AfterViewInit, OnDestroy {
    private session = inject(SessionService);
    private cdr = inject(ChangeDetectorRef);

    /**
     * The bar is one scrolling row rather than a wrapping block.
     *
     * An admin sees ten destinations, which on a phone wrapped to three rows and
     * pushed the page's own content most of a screen down. It scrolls at every
     * width rather than under a breakpoint: where the tabs already fit, there is
     * nothing to scroll and nothing to fade, so it looks exactly as it did.
     *
     * The two flags drive the edge fades — an affordance, so they have to mean
     * "there is more that way" rather than being painted on permanently.
     */
    scrolledStart = false;
    scrolledEnd = false;

    @ViewChild('nav') private navEl?: ElementRef<HTMLElement>;

    /** Tabs depend on role and labels on language, so the row's width is not
     *  settled at first paint. */
    private resizeObserver?: ResizeObserver;

    /** The row's width at the last resize, so only a change of width re-centres. */
    private lastWidth = 0;

    ngAfterViewInit(): void {
        const el = this.navEl?.nativeElement;
        if (!el) return;

        // No measure() here. This hook runs after the template has been checked,
        // so flags set now were not rendered until some later pass (and tripped
        // NG0100 in dev mode). The observer's first callback comes straight after
        // observe(), before the first paint, and measures then.
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.onResize());
            this.resizeObserver.observe(el);
        }
    }

    /**
     * A new width — rotating a phone, resizing a window — leaves scrollLeft
     * where it was, which after a rotation is somewhere unrelated to the tab
     * you are on. Width only: a height change would otherwise snap the row
     * back while someone is swiping through it.
     *
     * zone.js does not patch ResizeObserver, so this runs outside Angular and
     * nothing re-renders the fades unless asked. A re-centre that moves the row
     * gets a render from its scroll event, but one that does not (the tabs now
     * fit, say) would leave the old fades up.
     */
    private onResize(): void {
        const el = this.navEl?.nativeElement;
        if (!el) return;
        if (el.clientWidth !== this.lastWidth) {
            this.lastWidth = el.clientWidth;
            this.centreActive();
        }
        this.measure();
        this.cdr.detectChanges();
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    onScroll(): void {
        this.measure();
    }

    /**
     * Centres the tab you are on. Ten tabs run about two and a half screens at
     * phone width, so landing on a later one with the row parked at the start
     * reads as the current section missing from the nav.
     *
     * Hung off routerLinkActive's isActiveChange; it used to run in
     * ngAfterViewInit and never found anything. routerLinkActive adds its class
     * in a microtask — on a cold load not until the first NavigationEnd — so at
     * view init no tab was active yet. The event fires once the class is on.
     *
     * Measured from the two boxes rather than offsetLeft: the nav is not
     * positioned, so offsetLeft counts from <body> and was off by the page
     * gutter. scrollLeft rather than scrollIntoView(), which can scroll the
     * page vertically as well and move the content out from under the reader.
     */
    onActiveChange(isActive: boolean): void {
        if (isActive) this.centreActive();
    }

    private centreActive(): void {
        const el = this.navEl?.nativeElement;
        const active = el?.querySelector('a.active') as HTMLElement | null;
        if (!el || !active) return;

        const tab = active.getBoundingClientRect();
        const row = el.getBoundingClientRect();
        el.scrollLeft += (tab.left + tab.width / 2) - (row.left + row.width / 2);
        this.measure();
    }

    /** A few pixels of slack on both comparisons: scrollLeft is fractional on
     *  zoomed and hidpi displays, so an exact test can leave a fade up at the
     *  very end of the scroll. */
    private measure(): void {
        const el = this.navEl?.nativeElement;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        this.scrolledStart = el.scrollLeft > 4;
        this.scrolledEnd = max > 4 && el.scrollLeft < max - 4;
    }

    /** Account Details is reachable by anyone signed in — the page itself only
     *  checks isLoggedIn, and adapts for cloud customers. */
    get isLoggedIn(): boolean {
        return this.session.isLoggedIn();
    }

    /** Mirrors adminGuard. NOTE: a cloud session can carry isAdmin (legacy
     *  is_admin, or the FYES_FORCE_ADMIN dev flag), and the backend's
     *  fyesAdminRequired does not require isLocal either. */
    get isAdmin(): boolean {
        return this.session.isLoggedIn() && this.session.isAdmin();
    }

    /**
     * Mirrors dashboardGuard: `isLocal && canAccessOrders`. The isLocal half
     * matters — the dashboard acts on Fyes_AdminUsers identities (assignedTo,
     * resolvedBy), which a cloud session has no row for, so without it a cloud
     * admin would see Overview/Orders tabs that the guard then bounces.
     */
    get canAccessOrders(): boolean {
        return this.session.isLoggedIn() && this.session.isLocal() && this.session.canAccessOrders();
    }

    /**
     * The file-portal user: a local account with neither admin rights nor orders
     * access. Sales (canAccessOrders) is deliberately excluded — they get the
     * dashboard and nothing else.
     */
    get isFilePortalUser(): boolean {
        return this.session.isLoggedIn() && this.session.isLocal() && !this.session.canAccessOrders();
    }

    /** Cloud gateway customer: their own gateway list, never the admin tabs. */
    get isCloudCustomer(): boolean {
        return this.session.isLoggedIn() && !this.session.isLocal() && !this.session.isAdmin();
    }
}
